import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { ProductContext } from '../../ProductProvider';
import BooksSearch from './BooksSearch';
import JournalSearch from './journal/JournalSearch';
import BookCategoryCarosel from '../BookCategoryCarosel';
import './SearchMain.css';

const POPULAR_BOOKS_TOPICS = [
  { label: 'Society of Jesus', tab: 'books', topic: 'BookCategory', query: 'SOCIETY OF JESUS' },
  { label: 'Theology', tab: 'books', topic: 'BookCategory', query: 'THEOLOGY' },
  { label: 'Philosophy', tab: 'books', topic: 'BookCategory', query: 'PHILOSOPHY' },
  { label: 'Church History', tab: 'books', topic: 'BookCategory', query: 'CHURCH HISTORY' },
  { label: 'Spiritual Exercises', tab: 'books', topic: 'BookCategory', query: 'SPIRITUAL EXERCISES' },
  { label: 'Liturgy', tab: 'books', topic: 'BookCategory', query: 'LITURGY' },
  { label: 'Canon Law', tab: 'books', topic: 'BookCategory', query: 'CANON LAW' },
  { label: 'Priestly Spirituality', tab: 'books', topic: 'BookCategory', query: 'PRIESTLY SPIRITUALITY' }
];

const POPULAR_JOURNALS_ARTICLES_TOPICS = [
  { label: 'Theology', tab: 'journals', topic: 'JournalSubject', query: 'THEOLOGY' },
  { label: 'Jesuits', tab: 'journals', topic: 'JournalSubject', query: 'JESUITS' },
  { label: 'Pastoralia', tab: 'journals', topic: 'JournalSubject', query: 'PASTORALIA' },
  { label: 'Spirituality', tab: 'journals', topic: 'JournalSubject', query: 'SPIRITUALITY' },
  { label: 'Scripture', tab: 'journals', topic: 'JournalSubject', query: 'SCRIPTURE' },
  { label: 'Missiology', tab: 'journals', topic: 'JournalSubject', query: 'MISSIOLOGY' },
  { label: 'Liturgy', tab: 'journals', topic: 'JournalSubject', query: 'LITURGY' },
  { label: 'Canon Law', tab: 'journals', topic: 'JournalSubject', query: 'CANON LAW' },
  { label: 'Indian Church', tab: 'journals', topic: 'JournalSubject', query: 'INDIAN CHURCH' }
];

export default function SearchMain() {
  const { validateLogin, loggedUser } = useContext(ProductContext);

  const [selectedTab, setSelectedTab] = useState('books'); // 'books' | 'journals'
  const [activeQuery, setActiveQuery] = useState('');
  const [activeTopic, setActiveTopic] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    }
  }, [loggedUser]);

  // Scroll to top listener
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 250);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTopicClick = (topicItem) => {
    setSelectedTab(topicItem.tab);
    setActiveQuery(topicItem.query);
    setActiveTopic(topicItem.topic);
  };

  return (
    <div className="vj-search-portal">
      <Helmet>
        <title>Search Catalogue - Flynn - Meagher Library | Vidyajyoti College of Theology</title>
        <meta
          name="description"
          content="Search books, scholarly journals, theological theses, and articles in the Flynn - Meagher Library catalogue."
        />
        <meta
          name="keywords"
          content="Library Search, Book Catalogue, Theological Books, Vidyajyoti Library, Journal Search, Religious Studies"
        />
      </Helmet>

      {/* Hero Discovery Banner */}
      <section className="vj-search-hero">
        <div className="container">
          <div className="vj-search-hero-title">
            <i className="bi bi-compass me-2 text-info"></i> Library Discovery Portal
          </div>
          <p className="vj-search-hero-sub">
            Explore physical books on the shelf, scholarly journals, theological theses, and academic articles.
          </p>

          {/* Search Mode Pill Switcher */}
          <div className="vj-search-tab-group">
            <button
              type="button"
              className={`vj-search-tab-btn ${selectedTab === 'books' ? 'active' : ''}`}
              onClick={() => {
                setSelectedTab('books');
                setActiveQuery('');
                setActiveTopic('BookTitle');
              }}
              aria-label="Search Books Catalogue"
            >
              <i className="bi bi-book"></i> Books Catalogue
            </button>

            <button
              type="button"
              className={`vj-search-tab-btn ${selectedTab === 'journals' ? 'active' : ''}`}
              onClick={() => {
                setSelectedTab('journals');
                setActiveQuery('');
                setActiveTopic('JournalTitle');
              }}
              aria-label="Search Journals & Articles"
            >
              <i className="bi bi-journal-album"></i> Journals & Articles
            </button>
          </div>

          {/* Popular Topic Discovery Tags */}
          <div className="vj-topic-tags-container">
            <span className="vj-topic-tag-label">
              <i className="bi bi-stars me-1 text-warning"></i> Explore Topics:
            </span>
            {selectedTab === "journals" ? POPULAR_JOURNALS_ARTICLES_TOPICS.map((t, idx) => (
              <span
                key={idx}
                className="vj-topic-tag"
                onClick={() => handleTopicClick(t)}
                title={`Search ${t.query} in ${t.tab}`}
              >
                {t.label}
              </span>
            )) : POPULAR_BOOKS_TOPICS.map((t, idx) => (
              <span
                key={idx}
                className="vj-topic-tag"
                onClick={() => handleTopicClick(t)}
                title={`Search ${t.query} in ${t.tab}`}
              >
                {t.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Main Search Component */}
      <div className="container mt-5 pt-2">
        {selectedTab === 'books' ? (
          <BooksSearch initialQuery={activeQuery} initialTopic={activeTopic} />
        ) : (
          <JournalSearch initialQuery={activeQuery} initialTopic={activeTopic} />
        )}

        {/* Student Library Guide & Shelf Location Hint */}
        <div className="vj-discovery-tips-card">
          <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
            <i className="bi bi-lightbulb text-warning fs-5"></i> Quick Guide for Students: Finding Books on the Shelf
          </h6>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="vj-tip-item">
                <i className="bi bi-1-circle-fill vj-tip-icon"></i>
                <div className="small text-muted">
                  <strong>Search & Note Call Number:</strong> Look up your topic and note the <strong>Call No</strong> (e.g. <code>230.2/RAH</code>) on the book card.
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="vj-tip-item">
                <i className="bi bi-2-circle-fill vj-tip-icon"></i>
                <div className="small text-muted">
                  <strong>Locate the Stacks:</strong> Stacks are organized by Dewey Decimal classification numbers on the shelf ends.
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="vj-tip-item">
                <i className="bi bi-3-circle-fill vj-tip-icon"></i>
                <div className="small text-muted">
                  <strong>Issue at the Desk:</strong> Take your chosen books to the Circulation Counter for borrowing with your Library Card.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Carousel */}
        <div className="mt-4">
          <BookCategoryCarosel />
        </div>
      </div>

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            zIndex: 999,
            background: '#063547',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: '46px',
            height: '46px',
            boxShadow: '0 4px 12px rgba(6, 53, 71, 0.35)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            transition: 'all 0.2s ease',
          }}
          title="Scroll to Top"
        >
          <i className="bi bi-arrow-up"></i>
        </button>
      )}
    </div>
  );
}
